import { PAGE_SIZE } from '@/config/constants';
import { GET_ALL_FLYERS } from '@/graphql/queries';
import {
  districtVar,
  flyerboardActiveFiltersVar,
  flyerboardAllFiltersVar,
  flyerboardFlyersAmountVar,
} from '@/graphql/state';
import {
  AllFlyerFilters,
  GetAllFlyersInput,
  GetAllFlyersResponse,
  GetAllFlyersVars,
} from '@/graphql/types';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useMemo } from 'react';
import { useMetrics } from './useMetrics';

interface FlyerboardPagination {
  flyerboardFilters: AllFlyerFilters;
  flyerType?: string;
  audienceId?: string;
  districtId?: string | null;
  shouldUpdateFacets?: boolean;
  shouldTrackImpressions?: boolean;
  skip?: boolean;
  sortBy?: string;
}

export function useFlyerboardPagination(props: FlyerboardPagination) {
  const {
    flyerboardFilters,
    flyerType,
    audienceId,
    districtId: _districtId,
    shouldUpdateFacets = true,
    shouldTrackImpressions = true,
    skip = false,
    sortBy,
  } = props;

  const { trackImpressions } = useMetrics();

  const district = useReactiveVar(districtVar);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const districtId = useMemo(
    () => Number(_districtId || district?.districtId),
    [district, _districtId]
  );

  const queryInput = useMemo<GetAllFlyersInput | undefined>(() => {
    if (!districtId) {
      return;
    }
    const filters = {
      ...flyerboardFilters,
      districtId: Number(districtId),
    };
    const { types, grades } = flyerboardActiveFilters;
    if (types.length > 0) {
      filters.categories = [...types];
    }
    if (grades.length > 0) {
      filters.grades = [...grades];
    }
    const input: GetAllFlyersInput = {
      filters,
      pagination: {
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      },
    };
    if (sortBy) {
      input.sort = { sortBy };
    }
    return input;
  }, [flyerboardFilters, flyerboardActiveFilters, districtId, sortBy]);

  const { data, loading, fetchMore, called } = useQuery<GetAllFlyersResponse, GetAllFlyersVars>(
    GET_ALL_FLYERS,
    {
      skip: skip || queryInput === undefined,
      variables: {
        input: queryInput!,
      },
      onCompleted(data) {
        if (shouldUpdateFacets) {
          flyerboardAllFiltersVar(queryInput!.filters);
          flyerboardFlyersAmountVar(data.getAllFlyers.pagination.total);
        }

        if (shouldTrackImpressions && flyerType) {
          const { pageNumber, pageSize } = data.getAllFlyers.pagination;
          const slice = data.getAllFlyers.items.slice(
            (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          );
          trackImpressions({
            districtId: districtId.toString(),
            flyerType,
            audienceId,
            flyers: slice,
          });
        }
      },
    }
  );

  const flyers = data?.getAllFlyers.items ?? [];

  const dataNotFound = called && !loading && !skip && flyers.length === 0;
  const total = data?.getAllFlyers.pagination.total ?? 0;
  const page = data?.getAllFlyers.pagination.pageNumber ?? 1;
  const pages = Math.ceil(total / PAGE_SIZE);
  const isPaginationEnd = flyers.length === total;

  function fetchMoreFlyers() {
    fetchMore({
      variables: {
        input: {
          ...queryInput,
          pagination: {
            pageNumber: page + 1,
            pageSize: PAGE_SIZE,
          },
        },
      },
      updateQuery(previousData, { fetchMoreResult }) {
        return {
          getAllFlyers: {
            ...fetchMoreResult.getAllFlyers,
            items: [...previousData.getAllFlyers.items, ...fetchMoreResult.getAllFlyers.items],
          },
        };
      },
    });
  }

  return {
    loading,
    flyers,
    total,
    page,
    pages,
    isPaginationEnd,
    dataNotFound,
    fetchMoreFlyers,
  };
}
